import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const CategorySection = ({ categories = [], loading = false }) => {
    const navigate = useNavigate();
    const [parents, setParents] = useState([]);
    const [childMap, setChildMap] = useState({});
    const [selectedParent, setSelectedParent] = useState(null);
    const drawerRef = useRef(null);

    useEffect(() => {
        if (!categories.length) return;
        const p = categories.filter(c => c.parent_id === null);
        const map = {};
        categories.forEach(c => {
            if (c.parent_id !== null) {
                if (!map[c.parent_id]) map[c.parent_id] = [];
                map[c.parent_id].push(c);
            }
        });
        setParents(p);
        setChildMap(map);
    }, [categories]);

    const handleChildClick = (e, cat) => {
        e.preventDefault();
        e.stopPropagation();
        const navUrl = `/services?category_id=${cat.id}&category=${encodeURIComponent(cat.name)}`;
        navigate(navUrl);
        closeDrawer();
    };

    const openParent = (cat) => {
        if (selectedParent?.id === cat.id) closeDrawer();
        else setSelectedParent(cat);
    };

    const closeDrawer = () => setSelectedParent(null);

    useEffect(() => {
        if (selectedParent && drawerRef.current && window.innerWidth >= 769) {
            setTimeout(() => {
                const offsetTop = drawerRef.current.getBoundingClientRect().top + window.scrollY;
                window.scrollTo({ top: Math.max(0, offsetTop - 140), behavior: 'smooth' });
            }, 50);
        }
    }, [selectedParent]);

    const getChildCount = (id) => childMap[id]?.length || 0;
    const children = selectedParent ? (childMap[selectedParent.id] || []) : [];
    const isOpen = !!selectedParent;

    return (
        <>
            <style>{`
                .cat-section-wrap { position: relative; width: 100%; }
                
                /* Parent Grid */
                .pcat-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 20px; }
                @media (max-width: 540px) { .pcat-grid { grid-template-columns: repeat(3, 1fr); gap: 8px; } }
                
                .pcat-tile {
                    display: flex; flex-direction: column; align-items: center; gap: 8px;
                    cursor: pointer; padding: 15px 10px; border-radius: 14px;
                    border: 1.5px solid #eee; background: white; transition: all 0.2s;
                }
                .pcat-tile.active { border-color: var(--primary, #007bff); background: #f0f7ff; }
                .pcat-icon-bg { width: 45px; height: 45px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 1.4rem; }
                .pcat-tile-name { font-size: 0.8rem; font-weight: 600; text-align: center; }
                .pcat-tile-count { font-size: 0.65rem; color: #666; }

                /* Desktop Drawer Specifics */
                .cat-drawer-desktop { 
                    display: none; 
                    background: #fdfdfd; 
                    border: 1px solid #e0e0e0;
                    border-radius: 12px; 
                    margin-top: 10px;
                    animation: fadeIn 0.3s ease;
                }
                @media (min-width: 769px) {
                    .cat-drawer-desktop { display: block; }
                    .child-grid { 
                        display: grid; 
                        grid-template-columns: repeat(6, 1fr); /* More columns for desktop so it's not "too big" */
                        gap: 10px; 
                        padding: 20px;
                    }
                    .mobile-only { display: none !important; }
                }

                /* Sub-category Tiles (Desktop) */
                .child-tile {
                    display: flex; flex-direction: column; align-items: center; gap: 6px;
                    padding: 12px 8px; border-radius: 10px; border: 1px solid #eee;
                    background: white; cursor: pointer; transition: 0.2s;
                }
                .child-tile:hover { border-color: var(--primary, #007bff); transform: translateY(-2px); }
                .child-tile span:first-child { font-size: 1.1rem; }
                .child-tile span:last-child { font-size: 0.75rem; font-weight: 500; text-align: center; }

                /* Mobile Bottom Sheet */
                @media (max-width: 768px) {
                    .cat-drawer-desktop { display: none !important; }
                    .cat-sheet-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 1000; }
                    .cat-sheet {
                        position: fixed; bottom: 0; left: 0; right: 0; background: white;
                        border-radius: 20px 20px 0 0; z-index: 1001; max-height: 70vh;
                        display: flex; flex-direction: column; animation: sheetUp 0.3s ease;
                    }
                    .sheet-header { padding: 16px; border-bottom: 1px solid #eee; display: flex; justify-content: space-between; align-items: center; }
                    .sheet-body { padding: 15px; overflow-y: auto; }
                    .sheet-child-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
                    
                    /* Larger icons specifically for mobile readability */
                    .sheet-child-grid .child-tile { padding: 15px 5px; }
                    .sheet-child-grid .child-tile span:first-child { font-size: 1.5rem; }
                }

                @keyframes sheetUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            `}</style>

            <div className="cat-section-wrap">
                <div className="pcat-grid">
                    {parents.map(cat => (
                        <div key={cat.id} className={`pcat-tile ${selectedParent?.id === cat.id ? 'active' : ''}`} onClick={() => openParent(cat)}>
                            <div className="pcat-icon-bg" style={{ background: cat.color ? `${cat.color}20` : '#f0f7ff' }}>{cat.icon || '📦'}</div>
                            <div className="pcat-tile-name">{cat.name}</div>
                            <div className="pcat-tile-count">
                                {getChildCount(cat.id) > 0 ? `${getChildCount(cat.id)} types` : `${cat.service_count || 0} services`}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Desktop View: Strictly rendered in place */}
                {isOpen && (
                    <div className="cat-drawer-desktop" ref={drawerRef}>
                        <div className="child-grid">
                            {children.map(child => (
                                <div key={child.id} className="child-tile" onClick={(e) => handleChildClick(e, child)}>
                                    <span>{child.icon || '🔹'}</span>
                                    <span>{child.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Mobile View: Portal-like Overlay */}
            {isOpen && (
                <div className="mobile-only">
                    <div className="cat-sheet-backdrop" onClick={closeDrawer} />
                    <div className="cat-sheet">
                        <div className="sheet-header">
                            <strong style={{ fontSize: '1.1rem' }}>{selectedParent.name}</strong>
                            <button onClick={closeDrawer} style={{ border: 'none', background: 'none', fontSize: '1.5rem', color: '#999' }}>&times;</button>
                        </div>
                        <div className="sheet-body">
                            <div className="sheet-child-grid">
                                {children.map(child => (
                                    <div key={child.id} className="child-tile" onClick={(e) => handleChildClick(e, child)}>
                                        <span>{child.icon || '🔹'}</span>
                                        <span>{child.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CategorySection;